import { ChangeDetectionStrategy, Component, Inject, signal, WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { GusDialogCloseEvent, GusDialogConfig } from '@shared/modules/dialog/models/dialog.models';
import {
  PARTNER_SUBSCRIPTION_OFFER_DEFAULT_HERO_IMAGE,
  PartnerSubscriptionOfferDialogData,
} from '../../constants/partner-subscription-offer-dialog.constants';
import { DialogApi } from '@shared/modules/dialog/services/dialog.api';
import { CollectionSubscriptionDialogComponent } from '../collection-subscription-dialog/collection-subscription-dialog.component';
import { COLLECTION_SUBSCRIPTION_DIALOG_CONFIG } from '../../constants/collection-subscription-dialog.constants';
import { Subscription } from '@core/models/entities/subscription.model';
import { SubscriptionApi } from '@core/services/api/subscription.api';
import { SnackbarApiService } from '@core/services/helpers/snackbar-api.service';
import { User } from '@core/models/entities/user.models';

import { finalize } from 'rxjs';

@Component({
  selector: 'gus-partner-subscription-offer-dialog',
  templateUrl: './partner-subscription-offer-dialog.component.html',
  styleUrl: './partner-subscription-offer-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerSubscriptionOfferDialogComponent {
  readonly loading: WritableSignal<boolean> = signal(false);

  readonly user: User;

  private readonly subscriptionFromAccessCheck: Subscription | undefined;

  readonly paywallImageSrc: string;

  readonly paywallBackgroundImageCss: string;

  readonly paywallImageAriaLabel = 'Subscription offer';

  constructor(
    private readonly dialogRef: MatDialogRef<PartnerSubscriptionOfferDialogComponent, GusDialogCloseEvent>,
    private readonly dialogApi: DialogApi,
    private readonly subscriptionApi: SubscriptionApi,
    private readonly snackbarApiService: SnackbarApiService,
    @Inject(MAT_DIALOG_DATA) public readonly config: GusDialogConfig<PartnerSubscriptionOfferDialogData>
  ) {
    const data = config.data!;
    this.user = data.user;
    this.subscriptionFromAccessCheck = data.subscriptionFromAccessCheck;
    this.paywallImageSrc = PARTNER_SUBSCRIPTION_OFFER_DEFAULT_HERO_IMAGE;
    this.paywallBackgroundImageCss = `url("${this.paywallImageSrc.replace(/"/g, '')}")`;
  }

  skip(): void {
    this.dialogRef.close({ secondaryButtonClicked: true });
  }

  start(): void {
    const fromAccess = this.subscriptionFromAccessCheck;
    if (fromAccess?.web?.length) {
      this.openStripeDialog(fromAccess);
      return;
    }

    const catalog = this.user.subscriptions?.find(s => s.web?.length);
    if (catalog) {
      this.openStripeDialog(catalog);
      return;
    }

    this.loading.set(true);
    this.subscriptionApi
      .getCatalogSubscriptions()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (list: Subscription[]) => {
          const catalog = list?.find(s => s.web?.length);
          if (catalog) {
            this.openStripeDialog(catalog);
          } else {
            this.snackbarApiService.failure('Subscription checkout is unavailable. Please try again later.');
          }
        },
        error: () => {
          this.snackbarApiService.failure('Could not load subscription options. Please try again.');
        },
      });
  }

  private openStripeDialog(subscription: Subscription): void {
    this.dialogRef.close();
    this.dialogApi.openCustomDialog(
      CollectionSubscriptionDialogComponent,
      {
        ...COLLECTION_SUBSCRIPTION_DIALOG_CONFIG,
        data: subscription,
      },
      { width: '470px', maxWidth: 'calc(100vw - 32px)' }
    );
  }
}
